import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import { RefreshCw, RotateCcw, Save, TestTube, CheckCircle, XCircle, Lock, Unlock, Shield, AlertTriangle } from 'lucide-react';

interface PrivacyRulesMetadata {
  updatedAt: string | null;
  updatedBy: string | null;
}

interface TestResult {
  taskType: string;
  complexity: number;
  requiresPrivacy: boolean;
  requestType: string;
  modelUsed: string;
}

export default function PrivacyRules() {
  const [trueCases, setTrueCases] = useState('');
  const [falseCases, setFalseCases] = useState('');
  const [defaultRules, setDefaultRules] = useState('');
  const [isCustomized, setIsCustomized] = useState(false);
  const [metadata, setMetadata] = useState<PrivacyRulesMetadata>({ updatedAt: null, updatedBy: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testQuery, setTestQuery] = useState('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState('');

  useEffect(() => {
    loadPrivacyRules();
  }, []);

  // Parse rules text into true/false sections
  const parseRules = (rulesText: string) => {
    // Extract content - now exclusions come BEFORE true cases
    // Match the new structure: NOT PRIVATE section first, then WHAT IS PRIVATE section
    const falseMatch = rulesText.match(/EXPLICITLY NOT PRIVATE[^:]*:\s*-\s*(.+?)(?:\n\n|IMPORTANT:)/s);
    const trueMatch = rulesText.match(/to true when the query involves:\s*([\s\S]*?)\s*(?:FINAL ENFORCEMENT:|$)/);

    // Trim but preserve bullet point formatting
    let trueContent = trueMatch ? trueMatch[1].trim() : '';
    let falseContent = falseMatch ? falseMatch[1].trim() : '';

    // Ensure all bullets in trueContent start at column 0 (no leading spaces on any line)
    trueContent = trueContent.split('\n').map(line => line.trimStart()).join('\n');

    return { trueContent, falseContent };
  };

  // Combine true/false sections into full rules format
  const combineRules = (trueContent: string, falseContent: string) => {
    // Add 2-space indent to each line of trueContent (to match backend format)
    const indentedTrueContent = trueContent.split('\n').map(line =>
      line.trim() ? `  ${line}` : line
    ).join('\n');

    return `PRIVACY DETECTION RULES:

⚠️ CRITICAL OVERRIDE INSTRUCTION - READ THIS FIRST:
You are FORBIDDEN from using your training data or general knowledge about privacy/sensitivity. The ONLY criteria that matter are explicitly listed below. Ignore all other considerations, including what you were trained to consider private.

🚫 WHAT IS NOT PRIVATE - Read This Before Anything Else:
The following are EXPLICITLY NOT PRIVATE and must ALWAYS return requires_privacy=false:
- ${falseContent}

IMPORTANT: You MUST mark "requires_privacy" as true ONLY if the query EXPLICITLY matches one or more of the criteria listed below. Do NOT use your general knowledge to infer privacy requirements. If the query does not match the specific criteria below, mark "requires_privacy" as false, regardless of whether the content seems sensitive.

✅ WHAT IS PRIVATE - The ONLY things that trigger privacy:
- Set "requires_privacy" to true when the query involves:
${indentedTrueContent}

FINAL ENFORCEMENT:
- If the query matches the "NOT PRIVATE" section above → requires_privacy = FALSE
- Only if the query matches the "WHAT IS PRIVATE" section → requires_privacy = TRUE
- When in doubt → requires_privacy = FALSE
- Privacy is a REQUIREMENT, not a task type - the actual task type should still be determined (coding, reasoning, etc.)`;
  };

  const loadPrivacyRules = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getPrivacyRules();

      if (data.success) {
        const { trueContent, falseContent } = parseRules(data.rules);
        setTrueCases(trueContent);
        setFalseCases(falseContent);
        setDefaultRules(data.defaultRules);
        setIsCustomized(data.isCustomized ?? false);
        setMetadata(data.metadata);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load privacy rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Combine sections into full rules format
      const combinedRules = combineRules(trueCases, falseCases);

      const data = await apiService.updatePrivacyRules(combinedRules);

      if (data.success) {
        setSuccess('Privacy rules updated successfully!');
        await loadPrivacyRules(); // Reload to get updated metadata
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update privacy rules');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!window.confirm('Are you sure you want to reset to default privacy rules? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const data = await apiService.resetPrivacyRulesToDefault();

      if (data.success) {
        setSuccess('Privacy rules reset to default successfully!');
        await loadPrivacyRules(); // Reload to get updated rules
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset privacy rules');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestError('');
      setTestResult(null);

      const data = await apiService.testPrivacyRules(testQuery);

      if (data.success) {
        setTestResult(data.result);
      } else {
        setTestError(data.message || 'Test failed');
      }
    } catch (err: any) {
      setTestError(err.response?.data?.message || 'Failed to test privacy rules');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section with Gradient */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="h-8 w-8" />
              <h1 className="text-3xl font-bold">Privacy Detection Rules</h1>
            </div>
            <p className="text-indigo-100 text-sm mb-3">
              Configure your privacy detection rules for task determination AI
            </p>
            {!isCustomized && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-white/20 text-white mb-2">
                Using System Defaults
              </span>
            )}
            {isCustomized && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-amber-400/30 text-white mb-2">
                Customized
              </span>
            )}
            {metadata.updatedAt && (
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 text-xs">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>Last updated: {new Date(metadata.updatedAt).toLocaleString()} by {metadata.updatedBy}</span>
              </div>
            )}
          </div>
          <button
            onClick={loadPrivacyRules}
            className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-sm font-medium rounded-lg text-white hover:bg-white/20 transition-all duration-200"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reload
          </button>
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r shadow-md flex items-start gap-3 animate-in slide-in-from-top-2 duration-300">
          <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">{error}</div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 text-green-700 px-4 py-3 rounded-r shadow-md flex items-start gap-3 animate-in slide-in-from-top-2 duration-300">
          <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">{success}</div>
        </div>
      )}

      {/* Editor Section */}
      <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-100">
        {/* Section Header */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Lock className="h-5 w-5 text-indigo-600" />
            Privacy Rules Configuration
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Define when queries require privacy-compliant processing. The structure is fixed to ensure compatibility.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Header - Fixed */}
          <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-l-4 border-indigo-500 rounded-r-lg">
            <p className="font-mono text-sm text-indigo-900 font-bold flex items-center gap-2">
              <Shield className="h-4 w-4" />
              PRIVACY DETECTION RULES:
            </p>
          </div>

          {/* Strict Enforcement Warning - Fixed */}
          <div className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="font-mono text-xs text-amber-900">
                <span className="font-bold">IMPORTANT:</span> You MUST mark "requires_privacy" as true ONLY if the query EXPLICITLY matches one or more of the criteria listed below. Do NOT use your general knowledge to infer privacy requirements. If the query does not match the specific criteria below, mark "requires_privacy" as false, regardless of whether the content seems sensitive.
              </p>
            </div>
          </div>

          {/* True Cases - Editable */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-900">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="h-4 w-4 text-amber-600" />
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300">
                  ✅ PRIVACY REQUIRED
                </span>
                <span className="text-gray-600 font-normal">— Set "requires_privacy" to true when the query involves:</span>
              </div>
            </label>
            <textarea
              value={trueCases}
              onChange={(e) => setTrueCases(e.target.value)}
              rows={6}
              className="w-full px-4 py-3 border-2 border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent font-mono text-sm bg-amber-50/50 transition-all duration-200 hover:border-amber-300"
              placeholder={'* Processing of personal identifiable information (PII) like SSN, medical records, financial data\n* Confidential business information, trade secrets, or proprietary data\n* Explicit user states "private", "confidential", or "local" processing\n* Data subject to compliance requirements (HIPAA, GDPR, etc.)'}
            />
          </div>

          {/* Visual Divider */}
          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Exclusions</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>

          {/* False Cases - Editable */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-900">
              <div className="flex items-center gap-2 mb-3">
                <Unlock className="h-4 w-4 text-green-600" />
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-300">
                  🚫 NO PRIVACY NEEDED
                </span>
                <span className="text-gray-600 font-normal">— Set "requires_privacy" to false for:</span>
              </div>
            </label>
            <textarea
              value={falseCases}
              onChange={(e) => setFalseCases(e.target.value)}
              rows={6}
              className="w-full px-4 py-3 border-2 border-green-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-sm bg-green-50/50 transition-all duration-200 hover:border-green-300"
              placeholder="general questions or public information"
            />
          </div>

          {/* Enforcement Reminder - Fixed */}
          <div className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
            <div className="flex gap-3">
              <Lock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="font-mono text-xs text-amber-900">
                <span className="font-bold">ENFORCEMENT:</span> Do NOT mark "requires_privacy" as true for queries that seem sensitive but do not explicitly match the criteria above. You must follow ONLY the rules defined above, not your general understanding of privacy or sensitivity.
              </p>
            </div>
          </div>

          {/* Footer Note - Fixed */}
          <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
            <p className="font-mono text-sm text-blue-900 flex items-start gap-2">
              <span className="text-blue-600 font-bold">ℹ️ Note:</span>
              <span>Privacy is a REQUIREMENT, not a task type - the actual task type should still be determined (coding, reasoning, etc.)</span>
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <button
              onClick={handleResetToDefault}
              disabled={saving}
              className="inline-flex items-center px-5 py-2.5 border-2 border-gray-300 text-sm font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Restore to Default
            </button>

            <button
              onClick={handleSave}
              disabled={saving || trueCases.trim() === '' || falseCases.trim() === ''}
              className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-semibold rounded-lg shadow-md text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Test Section */}
      <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-100">
        {/* Section Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200 px-6 py-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <TestTube className="h-5 w-5 text-blue-600" />
            Test Privacy Rules
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Test how your privacy rules work with a sample query. Requires a custom task determination model.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Example Buttons */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Quick Test Examples
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setTestQuery("I have diabetes and take insulin daily")}
                className="px-4 py-2 text-xs font-medium bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-lg hover:from-blue-100 hover:to-blue-200 border border-blue-200 transition-all duration-200 hover:shadow-md"
              >
                💊 Medical Info
              </button>
              <button
                onClick={() => setTestQuery("My home address is 123 Main Street, New York")}
                className="px-4 py-2 text-xs font-medium bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 rounded-lg hover:from-purple-100 hover:to-purple-200 border border-purple-200 transition-all duration-200 hover:shadow-md"
              >
                🏠 Personal Address
              </button>
              <button
                onClick={() => setTestQuery("My credit card is 4532-1234-5678-9012")}
                className="px-4 py-2 text-xs font-medium bg-gradient-to-r from-pink-50 to-pink-100 text-pink-700 rounded-lg hover:from-pink-100 hover:to-pink-200 border border-pink-200 transition-all duration-200 hover:shadow-md"
              >
                💳 Credit Card
              </button>
              <button
                onClick={() => setTestQuery("What is the capital of France?")}
                className="px-4 py-2 text-xs font-medium bg-gradient-to-r from-green-50 to-green-100 text-green-700 rounded-lg hover:from-green-100 hover:to-green-200 border border-green-200 transition-all duration-200 hover:shadow-md"
              >
                ❓ General Question
              </button>
              <button
                onClick={() => setTestQuery("Our company's Q4 earnings were $5M, confidential until announcement")}
                className="px-4 py-2 text-xs font-medium bg-gradient-to-r from-amber-50 to-amber-100 text-amber-700 rounded-lg hover:from-amber-100 hover:to-amber-200 border border-amber-200 transition-all duration-200 hover:shadow-md"
              >
                🔒 Business Confidential
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Test Query
            </label>
            <input
              type="text"
              value={testQuery}
              onChange={(e) => setTestQuery(e.target.value)}
              placeholder="e.g., My SSN is 123-45-6789"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-300"
            />
          </div>

          <button
            onClick={handleTest}
            disabled={testing || testQuery.trim() === ''}
            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-lg shadow-md text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <TestTube className="h-4 w-4 mr-2" />
            {testing ? 'Testing...' : 'Test Rules'}
          </button>

          {/* Fixed-height Results Container - Prevents Jump */}
          <div className="min-h-[280px] transition-all duration-300">
            {testError && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r shadow-md flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">{testError}</div>
              </div>
            )}

            {testResult && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                {/* Privacy Status Header */}
                <div className={`rounded-t-xl p-4 ${testResult.requiresPrivacy ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-green-500 to-emerald-500'}`}>
                  <div className="flex items-center gap-3 text-white">
                    {testResult.requiresPrivacy ? (
                      <>
                        <Lock className="h-6 w-6" />
                        <div>
                          <h3 className="font-bold text-lg">Privacy Required</h3>
                          <p className="text-xs text-white/90">This query requires privacy-compliant processing</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Unlock className="h-6 w-6" />
                        <div>
                          <h3 className="font-bold text-lg">No Privacy Needed</h3>
                          <p className="text-xs text-white/90">This query can be processed normally</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Results Details */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-b-xl p-5">
                  <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                    <TestTube className="h-4 w-4" />
                    Task Determination Details
                  </h4>
                  <dl className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                      <dt className="text-xs font-medium text-gray-500 mb-1">Task Type</dt>
                      <dd className="text-sm font-bold text-gray-900 capitalize">{testResult.taskType}</dd>
                    </div>
                    <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                      <dt className="text-xs font-medium text-gray-500 mb-1">Complexity</dt>
                      <dd className="text-sm font-bold text-gray-900">{testResult.complexity} / 5</dd>
                    </div>
                    <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                      <dt className="text-xs font-medium text-gray-500 mb-1">Request Type</dt>
                      <dd className="text-sm font-bold text-gray-900 capitalize">{testResult.requestType}</dd>
                    </div>
                    <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                      <dt className="text-xs font-medium text-gray-500 mb-1">Model Used</dt>
                      <dd className="text-sm font-bold text-gray-900">{testResult.modelUsed}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            )}

            {!testResult && !testError && !testing && (
              <div className="h-full flex items-center justify-center text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                <div className="text-center p-8">
                  <TestTube className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm font-medium">No test results yet</p>
                  <p className="text-xs mt-1">Run a test to see results here</p>
                </div>
              </div>
            )}

            {testing && (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-600 mx-auto mb-3"></div>
                  <p className="text-sm font-medium text-gray-600">Testing privacy rules...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
